/**
 * TANITIM VE ANA SAYFA KARTLARI (10.10.2026; taslak `claude/15-dashboard-taslak.md`,
 * kararlar `claude/notlar/2026-10-10-dashboard-ve-lisans-taslaklari.md`).
 *
 * Aynı kartlar iki yerde:
 *  - defter açılmadan önce tanıtım sayfasında (`TanitimSayfasi`) — yalnızca tanıtım;
 *  - defter açılınca ana sayfada (`AnaSayfaScreen`) — kart, defterin İÇERİĞİNE
 *    göre özetine döner. "Hangi tanıtım kapandı" ayrıca SAKLANMAZ: her açılışta
 *    defterden okunur (kilitliyken dışarıda iz kalmaz).
 *
 * Renkler süs (`deco-*`): anlam taşımaz. Uyarı rengi yalnızca gerçek bir
 * anormallikte (ör. veri var, yedek yok) kartın içinde kullanılır.
 */

import { useId, useState, type ReactNode } from "react";
import { cx } from "../../ui/primitives";
import { M } from "../../dil";

export type KartRengi = 1 | 2 | 3 | 4;

export type KartKimligi = "defter" | "baslangic" | "fiyatlar" | "raporlar" | "yedek" | "sanal";

export interface KartTanimi {
  readonly kimlik: KartKimligi;
  readonly baslik: string;
  readonly renk: KartRengi;
  /** Tanıtım metni: özelliğin ne sunduğu, ürün dilinde. */
  readonly tanitim: string;
}

/**
 * Sıra ve başlıklar (kullanıcı, 10.10.2026; çerçeve, kesin değil): Gizlilik ile
 * Profiller tek kart ve en üstte; defteri kurma/açma eylemi onun içinde.
 */
export const KARTLAR: Readonly<Record<KartKimligi, KartTanimi>> = {
  defter: {
    kimlik: "defter",
    baslik: M.anaSayfa.kart.defter.baslik,
    renk: 4,
    tanitim: M.anaSayfa.kart.defter.tanitim,
  },
  baslangic: {
    kimlik: "baslangic",
    baslik: M.anaSayfa.kart.baslangic.baslik,
    renk: 1,
    tanitim: M.anaSayfa.kart.baslangic.tanitim,
  },
  fiyatlar: {
    kimlik: "fiyatlar",
    baslik: M.anaSayfa.kart.fiyatlar.baslik,
    renk: 2,
    tanitim: M.anaSayfa.kart.fiyatlar.tanitim,
  },
  raporlar: {
    kimlik: "raporlar",
    baslik: M.anaSayfa.kart.raporlar.baslik,
    renk: 3,
    tanitim: M.anaSayfa.kart.raporlar.tanitim,
  },
  yedek: {
    kimlik: "yedek",
    baslik: M.anaSayfa.kart.yedek.baslik,
    renk: 2,
    tanitim: M.anaSayfa.kart.yedek.tanitim,
  },
  sanal: {
    kimlik: "sanal",
    baslik: M.anaSayfa.kart.sanal.baslik,
    renk: 3,
    tanitim: M.anaSayfa.kart.sanal.tanitim,
  },
};

const ZEMIN: Readonly<Record<KartRengi, string>> = {
  1: "bg-deco-1-soft text-deco-1",
  2: "bg-deco-2-soft text-deco-2",
  3: "bg-deco-3-soft text-deco-3",
  4: "bg-deco-4-soft text-deco-4",
};

/** Dolu simge dairesi: kartın rengi zemin, çizgi beyaz (koyu temada koyu). */
const DOLU: Readonly<Record<KartRengi, string>> = {
  1: "bg-deco-1 text-deco-fg",
  2: "bg-deco-2 text-deco-fg",
  3: "bg-deco-3 text-deco-fg",
  4: "bg-deco-4 text-deco-fg",
};

/** Kartın zemini: rengin çok açık tonundan yüzeye (göze çarpan ama okunur). */
const KART_ZEMINI: Readonly<Record<KartRengi, string>> = {
  1: "bg-gradient-to-b from-deco-1-soft to-surface",
  2: "bg-gradient-to-b from-deco-2-soft to-surface",
  3: "bg-gradient-to-b from-deco-3-soft to-surface",
  4: "bg-gradient-to-b from-deco-4-soft to-surface",
};

const SERIT: Readonly<Record<KartRengi, string>> = {
  1: "bg-deco-1",
  2: "bg-deco-2",
  3: "bg-deco-3",
  4: "bg-deco-4",
};

/** Basit çizgi simgeler (24×24, `currentColor`). */
const SIMGE_YOLU: Readonly<Record<KartKimligi, string>> = {
  baslangic: "M4 6h16M4 12h16M4 18h10M18 16v4M16 18h4",
  fiyatlar: "M4 19V5M4 19h16M7 15l4-4 3 3 5-6",
  raporlar: "M6 20V10M12 20V4M18 20v-7",
  sanal: "M12 3a9 9 0 1 0 9 9M12 7v5l3 2M17 3l4 4-4 4",
  defter: "M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6l7-3Zm-3 9 2 2 4-4",
  yedek: "M7 18a4 4 0 0 1-.6-7.96A6 6 0 0 1 18 9a4.5 4.5 0 0 1-.5 9H7Zm5-7v6m-2.5-2.5L12 18l2.5-2.5",
};

export function KartSimgesi({ kimlik, renk }: { readonly kimlik: KartKimligi; readonly renk: KartRengi }) {
  return (
    <span aria-hidden="true" className={cx("inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full shadow-sm", DOLU[renk])}>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d={SIMGE_YOLU[kimlik]} />
      </svg>
    </span>
  );
}

/**
 * Tek kart. `ozet` verilmezse kart TANITIMDIR (metin + eylem). Verilirse özet
 * gösterilir ve köşedeki "?" kartı GEÇİCİ olarak tanıtımına döndürür (durum
 * saklanmaz; sayfa yenilenince özet).
 */
export function Kart({
  kimlik,
  baslik,
  renk,
  tanitim,
  ozet,
  eylem,
  gorsel,
  vurgulu = false,
}: {
  readonly kimlik: KartKimligi;
  readonly baslik: string;
  readonly renk: KartRengi;
  readonly tanitim: ReactNode;
  readonly ozet?: ReactNode;
  readonly eylem?: ReactNode;
  /** Tanıtımın yanında bir görsel (yalnızca tanıtım sayfasında; dar ekranda altına iner). */
  readonly gorsel?: ReactNode;
  /** Gizlilik ve profiller kartı (defter eylemi içinde): geniş. */
  readonly vurgulu?: boolean;
}) {
  const [tanitimAcik, setTanitimAcik] = useState(false);
  const baslikId = useId();
  const tanitimGoster = ozet === undefined || tanitimAcik;
  return (
    <section
      aria-labelledby={baslikId}
      className={cx("pf-card relative flex flex-col overflow-hidden", KART_ZEMINI[renk], vurgulu ? "sm:col-span-2" : "")}
    >
      <span aria-hidden="true" className={cx("h-1.5 w-full", SERIT[renk])} />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <KartSimgesi kimlik={kimlik} renk={renk} />
          <h2 id={baslikId} className={cx("flex-1 pt-1.5 font-semibold text-ink", vurgulu ? "text-[17px]" : "text-[15px]")}>
            {baslik}
          </h2>
          {ozet !== undefined && (
            <button
              type="button"
              aria-label={tanitimAcik ? M.anaSayfa.kart.ozeteDon(baslik) : M.anaSayfa.kart.neIseYarar(baslik)}
              aria-pressed={tanitimAcik}
              onClick={() => setTanitimAcik((x) => !x)}
              className={cx(
                "h-6 w-6 shrink-0 rounded-full border text-[12px] font-semibold",
                tanitimAcik ? "border-transparent " + ZEMIN[renk] : "border-line text-faint hover:text-ink",
              )}
            >
              ?
            </button>
          )}
        </div>
        {tanitimGoster && gorsel !== undefined ? (
          <div className="grid items-center gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
            <p className="text-[13px] leading-relaxed text-muted">{tanitim}</p>
            <div className="flex justify-center">{gorsel}</div>
          </div>
        ) : tanitimGoster ? (
          <p className="text-[13px] leading-relaxed text-muted">{tanitim}</p>
        ) : (
          <div className="text-[13px] text-ink">{ozet}</div>
        )}
        {eylem !== undefined && <div className="mt-auto flex flex-wrap gap-2 pt-1">{eylem}</div>}
      </div>
    </section>
  );
}
